// pages/YearPlan.js
import { useParams, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';

const DEFAULT_DOMAIN = 'toshba';

export default function YearPlan() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // domain: 'talmud' or 'toshba'
  const domain = (searchParams.get('domain') || localStorage.getItem('domain') || DEFAULT_DOMAIN).toLowerCase();

  // Prefer name passed via navigation state (from TractateDetail); otherwise fetch it.
  const [tractateName, setTractateName] = useState(location.state?.tractateName || '');
  const [nameLoading, setNameLoading] = useState(!location.state?.tractateName);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch the yearly plans (filtered by domain)
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/yearly_plans/?domain=${encodeURIComponent(domain)}`);
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        if (!ignore) setPlans(data);
      } catch {
        if (!ignore) setError('שגיאה בטעינת התוכנית השנתית');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, domain]);

  // Fetch tractate name if not provided via state (deep link)
  useEffect(() => {
    if (tractateName) { setNameLoading(false); return; }
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/`);
        if (res.ok) {
          const data = await res.json();
          if (!ignore) setTractateName(data?.name || '');
        }
      } finally {
        if (!ignore) setNameLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, tractateName]);

  function fileNameFromUrl(url) {
    try {
      const u = new URL(url, window.location.origin);
      return decodeURIComponent(u.pathname.split('/').pop() || 'קובץ');
    } catch {
      return 'קובץ';
    }
  }

  // ---- Domain-aware breadcrumb label + domain-preserving list link ----
  const domText = domain === 'talmud' ? 'תלמוד' : 'תושב"ע';
  const tractateCrumbLabel = tractateName ? `${tractateName} (${domText})` : 'מסכת';
  const tractatesHref = `/tractates?domain=${domain}`;

  return (
    <div className="container">
      <Breadcrumbs
        items={[
          { to: tractatesHref, label: 'מסכתות' },
          { to: `/tractates/${id}?domain=${domain}`, label: tractateCrumbLabel },
          { label: 'תוכנית שנתית' },
        ]}
      />

      <div className="toolbar toolbar--center">
        <h2 style={{ marginInlineStart: 0 }}>
          תוכנית ותכנון שנתי{tractateName ? ` — ${tractateName}` : ''}
        </h2>
      </div>

      <p className="center" style={{ marginTop: '.5rem' }}>
        {/* keep the chosen domain & pass name back for smoother breadcrumbs */}
        <Link to={`/tractates/${id}?domain=${domain}`} state={{ tractateName }}>
          חזרה למסכת
        </Link>
      </p>

      {(loading || nameLoading) && <p className="muted">טוען…</p>}
      {error && <p style={{ color: 'crimson' }}>{error}</p>}

      {!loading && !error && plans.length === 0 && (
        <p className="muted">אין כרגע קבצים להצגה.</p>
      )}

      {!loading && !error && plans.length > 0 && (
        <div className="grid-autofit">
          {plans.map((p) => (
            <article key={p.id} className="c-card">
              {p.image ? (
                <img className="c-card__media" src={p.image} alt="תצוגה מקדימה" />
              ) : (
                <div className="c-card__media" />
              )}
              <div className="c-card__body">
                <h3 className="c-card__title">{p.name || fileNameFromUrl(p.file)}</h3>
                <p className="c-card__meta">קובץ תוכנית שנתית ({p.domain})</p>
                <div style={{ marginTop: '0.75rem' }}>
                  <a className="btn" href={p.file} target="_blank" rel="noreferrer" aria-label={`פתיחת ${fileNameFromUrl(p.file)}`}>
                    פתיחת קובץ
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
